import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import {
  sendSupportBookedEventJoinLink,
  updateSupportBookedEventRtcProvider,
} from 'src/supportPortal/services/supportBookedEventActionsService'
import { isSupportSessionStaleInProgress } from 'src/mentor/components/MentorEventsPage/components/SupportBookingCardParts'
import SendPaymentLinkDialog from 'src/shared/patientActions/SendPaymentLinkDialog'

const RTC_PROVIDER_LABELS = {
  agora: 'Agora',
  hms: '100ms',
  daily: 'Daily',
  zoom: 'Zoom',
}

export function isSupportBookedEventCompleted(bookedEvent) {
  return Boolean(
    bookedEvent?.markedCompleted ||
      bookedEvent?.sessionStatus === 'marked-completed' ||
      bookedEvent?.sessionStatus === 'finished',
  )
}

function getRecordingPolicySourceLabel(source) {
  switch (source) {
    case 'event':
      return 'Event override'
    case 'patient':
      return 'Patient default'
    case 'default-off':
      return 'Default off'
    default:
      return null
  }
}

/** Media/recording capability flags for one booked event, given the viewer's roles. */
export function getSessionMediaCapabilities(
  bookedEvent,
  hasVcAuditAccess,
  canManageRecordingPreferenceForRole = false,
) {
  const completed = isSupportBookedEventCompleted(bookedEvent)
  const canManageRecordingPreference =
    !completed &&
    canManageRecordingPreferenceForRole &&
    bookedEvent?.canManageRecordingPreference === true
  const isRecordingEnabled = bookedEvent?.resolvedRecordingEnabled === true
  const sourceLabel = getRecordingPolicySourceLabel(bookedEvent?.resolvedRecordingPolicySource)
  // A session the backend still reports as live (its live window runs up to ~8h from start) but which
  // the card has relabelled "Started, not completed" (past endTime + buffer) has nothing live to watch —
  // so never offer the Watch Live button on an incomplete/stale card.
  const canPlayLive =
    hasVcAuditAccess &&
    bookedEvent?.vcAuditVisible &&
    bookedEvent?.vcAuditCanViewLive === true &&
    !isSupportSessionStaleInProgress(bookedEvent)
  const recordingCount = Number(bookedEvent?.vcAuditRecordingCount || 0)
  const canPlayRecording =
    hasVcAuditAccess &&
    bookedEvent?.vcAuditVisible &&
    bookedEvent?.vcAuditCanViewRecordings === true &&
    recordingCount > 0
  const latestRecordingId = bookedEvent?.vcAuditLatestRecordingId

  return {
    canManageRecordingPreference,
    canPlayLive,
    canPlayRecording,
    hasAnyMediaAction: canManageRecordingPreference || canPlayLive || canPlayRecording,
    isRecordingEnabled,
    latestRecordingId,
    recordingCount,
    sourceLabel,
  }
}

/**
 * Compact grouped-action button for the support booking card: same footprint as the
 * card's ghost action buttons, opens a small anchored menu.
 */
export function SupportActionMenuButton({ label, icon, badgeDot = false, title = '', children }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const closeMenu = () => setAnchorEl(null)

  const items = (Array.isArray(children) ? children.flat() : [children]).filter(Boolean)
  if (items.length === 0) {
    return null
  }

  const button = (
    <Button
      size="small"
      variant="outlined"
      color="inherit"
      startIcon={icon}
      endIcon={<ExpandMoreRoundedIcon sx={{ fontSize: '16px !important', color: '#94a3b8' }} />}
      onClick={(event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
      }}
      sx={{
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 650,
        fontSize: 12.5,
        minHeight: 30,
        px: 1.15,
        py: 0.25,
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
        flexShrink: 0,
        color: '#334155',
        borderColor: anchorEl ? 'rgba(79, 70, 229, 0.4)' : 'rgba(148, 163, 184, 0.45)',
        backgroundColor: anchorEl ? '#f8fafc' : '#ffffff',
        boxShadow: 'none',
        position: 'relative',
        '& .MuiButton-startIcon': { mr: 0.5, ml: -0.25 },
        '& .MuiButton-endIcon': { ml: 0.35, mr: -0.4 },
        '&:hover': { boxShadow: 'none', backgroundColor: '#f8fafc' },
      }}
    >
      {label}
      {badgeDot ? (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: '#ef4444',
            animation: 'support-menu-live-pulse 1.6s ease-in-out infinite',
            '@keyframes support-menu-live-pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.35 },
            },
          }}
        />
      ) : null}
    </Button>
  )

  return (
    <>
      {title ? (
        <Tooltip title={title}>
          <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
            {button}
          </Box>
        </Tooltip>
      ) : (
        button
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        onClick={(event) => event.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              minWidth: 248,
              border: '1px solid rgba(226, 232, 240, 0.9)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.14)',
              '& .MuiMenuItem-root': { py: 0.85 },
              '& .MuiListItemText-primary': { fontSize: 13.5, fontWeight: 600, color: '#1e293b' },
              '& .MuiListItemText-secondary': { fontSize: 11.5 },
              '& .MuiListItemIcon-root': { minWidth: 32 },
            },
          },
        }}
      >
        {items.map((item) =>
          typeof item === 'function' ? item(closeMenu) : item,
        )}
      </Menu>
    </>
  )
}

/**
 * "Billing" group: fee details, payment links and invoice actions in one menu.
 * All dialogs are hosted by the page; this component only routes intent.
 */
export function SupportBillingMenu({
  bookedEvent,
  onFeeDetails,
  onPaymentLinks,
  canManageInvoices = false,
  onGenerateInvoice,
  onSendInvoice,
  canMarkPaid = false,
  onMarkPaid,
}) {
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false)
  const isCancelled = bookedEvent?.status === 'cancelled'
  const isPaymentPending =
    bookedEvent?.status === 'booking-initiated' && bookedEvent?.paymentStatus === 'pending'
  const hasPaymentLinks = Boolean(bookedEvent?.paymentLinks?.length) || isPaymentPending
  const showMarkPaid = canMarkPaid && isPaymentPending && !isCancelled && Boolean(onMarkPaid)
  const canSendPaymentLink = !isCancelled && Boolean(bookedEvent?.id)

  // Contact context for the create-and-send payment-link dialog (support source → the send runs on the
  // booking practitioner's behalf, with an agent-chosen "from" name).
  const paymentContactContext = {
    source: 'support-patient',
    bookedEventId: bookedEvent?.id,
    patientId: bookedEvent?.patientId,
    clinicBrandId: bookedEvent?.clinicBrandId,
    clinicId: bookedEvent?.clinicId,
    clinicBrandName: bookedEvent?.clinicBrandName,
    patientName: bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Patient',
    patientMobile: bookedEvent?.patientInfoData?.mobile,
    patientEmail: bookedEvent?.patientInfoData?.email,
  }

  return (
    <>
    <SupportActionMenuButton
      label="Billing"
      icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 15 }} />}
      title="Fee, payment links and invoices"
    >
      {(close) => (
        <MenuItem
          key="fee-details"
          onClick={() => {
            close()
            onFeeDetails?.(bookedEvent)
          }}
        >
          <ListItemIcon>
            <ReceiptLongOutlinedIcon fontSize="small" sx={{ color: '#475569' }} />
          </ListItemIcon>
          <ListItemText primary="Fee details" secondary="Amount, method, collection metadata" />
        </MenuItem>
      )}
      {canSendPaymentLink
        ? (close) => (
            <MenuItem
              key="send-payment-link"
              onClick={() => {
                close()
                setPaymentLinkDialogOpen(true)
              }}
            >
              <ListItemIcon>
                <PaymentsOutlinedIcon fontSize="small" sx={{ color: '#4338ca' }} />
              </ListItemIcon>
              <ListItemText primary="Send payment link" secondary="Create & send a Razorpay link" />
            </MenuItem>
          )
        : null}
      {hasPaymentLinks && onPaymentLinks
        ? (close) => (
            <MenuItem
              key="payment-links"
              onClick={() => {
                close()
                onPaymentLinks(bookedEvent)
              }}
            >
              <ListItemIcon>
                <PaymentsOutlinedIcon fontSize="small" sx={{ color: '#b45309' }} />
              </ListItemIcon>
              <ListItemText
                primary="Payment links"
                secondary={isPaymentPending ? 'Payment is still pending' : undefined}
              />
            </MenuItem>
          )
        : null}
      {showMarkPaid
        ? (close) => (
            <MenuItem
              key="mark-paid"
              onClick={() => {
                close()
                onMarkPaid(bookedEvent)
              }}
            >
              <ListItemIcon>
                <PaidRoundedIcon fontSize="small" sx={{ color: '#047857' }} />
              </ListItemIcon>
              <ListItemText primary="Mark as paid" secondary="Confirm an offline payment" />
            </MenuItem>
          )
        : null}
      {canManageInvoices ? <Divider key="invoice-divider" /> : null}
      {canManageInvoices && onGenerateInvoice
        ? (close) => (
            <MenuItem
              key="generate-invoice"
              onClick={() => {
                close()
                onGenerateInvoice(bookedEvent)
              }}
            >
              <ListItemIcon>
                <PostAddOutlinedIcon fontSize="small" sx={{ color: '#475569' }} />
              </ListItemIcon>
              <ListItemText primary="Generate invoice" />
            </MenuItem>
          )
        : null}
      {canManageInvoices && onSendInvoice
        ? (close) => (
            <MenuItem
              key="send-invoice"
              onClick={() => {
                close()
                onSendInvoice(bookedEvent)
              }}
            >
              <ListItemIcon>
                <SendOutlinedIcon fontSize="small" sx={{ color: '#475569' }} />
              </ListItemIcon>
              <ListItemText primary="Send invoice" secondary="Generate and share with the patient" />
            </MenuItem>
          )
        : null}
    </SupportActionMenuButton>
    <SendPaymentLinkDialog
      open={paymentLinkDialogOpen}
      onClose={() => setPaymentLinkDialogOpen(false)}
      contactContext={paymentContactContext}
      patientName={paymentContactContext.patientName}
    />
    </>
  )
}

/**
 * "Session" group: joining links, live/recording playback, the recording preference
 * toggle, video-provider override and participant logs. Owns the send-join-link and
 * provider dialogs; link/log dialogs are hosted by the page.
 */
export function SupportSessionMenu({
  bookedEvent,
  hasVcAuditAccess = false,
  hasRecordingPreferenceAccess = false,
  onJoiningLinks,
  onActionSuccess,
}) {
  const navigate = useNavigate()
  const [joinLinkDialogOpen, setJoinLinkDialogOpen] = useState(false)
  const [joinLinkKind, setJoinLinkKind] = useState('secure')
  const [joinLinkCustomMobile, setJoinLinkCustomMobile] = useState('')
  const [joinLinkSending, setJoinLinkSending] = useState(false)
  const [rtcDialogOpen, setRtcDialogOpen] = useState(false)
  const [rtcSelection, setRtcSelection] = useState('')
  const [rtcSaving, setRtcSaving] = useState(false)
  const [recordingToggling, setRecordingToggling] = useState(false)

  const isCancelled = bookedEvent?.status === 'cancelled'
  const isOnline = bookedEvent?.meetingSrc === 'aumhum'
  const mediaCapabilities = getSessionMediaCapabilities(
    bookedEvent,
    hasVcAuditAccess,
    hasRecordingPreferenceAccess,
  )
  const canShowJoiningLinks = isOnline && !isCancelled && Boolean(onJoiningLinks)
  const canSendJoinLink = isOnline && !isCancelled
  const rtcProviderOptions = bookedEvent?.validRtcProviderOptions || []
  const canChangeRtcProvider =
    isOnline && bookedEvent?.rtcProviderOverrideEditable === true && rtcProviderOptions.length > 0

  const hasAnyItem =
    canShowJoiningLinks ||
    canSendJoinLink ||
    mediaCapabilities.hasAnyMediaAction ||
    canChangeRtcProvider

  if (!hasAnyItem) {
    return null
  }

  async function handleRecordingToggle(close) {
    if (!bookedEvent?.id || recordingToggling) {
      return
    }
    setRecordingToggling(true)
    const loadingToast = toast.loading('Updating recording preference...')
    const apiUrl = mediaCapabilities.isRecordingEnabled
      ? '/api/secure/cc/support/events/mark-event-as-not-recordable'
      : '/api/secure/cc/support/events/mark-event-as-recordable'
    try {
      const response = await putSPDataWithParams(apiUrl, null, {
        'booked-event-id': bookedEvent.id,
      })
      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        toast.update(loadingToast, {
          render: mediaCapabilities.isRecordingEnabled
            ? 'Recording disabled for this event'
            : 'Recording enabled for this event',
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        })
        close()
        onActionSuccess?.()
      } else {
        toast.update(loadingToast, {
          render: response?.data?.message || 'Could not update recording preference',
          type: 'error',
          isLoading: false,
          autoClose: 3500,
        })
      }
    } catch (error) {
      toast.update(loadingToast, {
        render: 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } finally {
      setRecordingToggling(false)
    }
  }

  async function handleSendJoinLink(toHumanSupport = false) {
    setJoinLinkSending(true)
    const result = await sendSupportBookedEventJoinLink({
      bookedEventId: bookedEvent?.id,
      kind: joinLinkKind,
      toCustomMobile: joinLinkCustomMobile.trim() || undefined,
      toHumanSupport,
    })
    setJoinLinkSending(false)
    if (result.ok) {
      toast.success(
        toHumanSupport
          ? 'Join link sent to the human-support WhatsApp'
          : joinLinkCustomMobile.trim()
            ? 'Join link sent to the custom number'
            : "Join link sent to the patient's WhatsApp",
      )
      setJoinLinkDialogOpen(false)
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  async function handleRtcProviderSave() {
    setRtcSaving(true)
    const result = await updateSupportBookedEventRtcProvider({
      bookedEventId: bookedEvent?.id,
      rtcProviderOverride: rtcSelection || null,
    })
    setRtcSaving(false)
    if (result.ok) {
      toast.success('Video provider updated')
      setRtcDialogOpen(false)
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  return (
    <>
      <SupportActionMenuButton
        label="Session"
        icon={<VideocamOutlinedIcon sx={{ fontSize: 15 }} />}
        badgeDot={mediaCapabilities.canPlayLive}
        title="Joining links, recordings and session controls"
      >
        {canShowJoiningLinks
          ? (close) => (
              <MenuItem
                key="joining-links"
                onClick={() => {
                  close()
                  onJoiningLinks(bookedEvent)
                }}
              >
                <ListItemIcon>
                  <LinkRoundedIcon fontSize="small" sx={{ color: '#4f46e5' }} />
                </ListItemIcon>
                <ListItemText primary="Joining links" secondary="View, copy or open" />
              </MenuItem>
            )
          : null}
        {canSendJoinLink
          ? (close) => (
              <MenuItem
                key="send-join-link"
                onClick={() => {
                  close()
                  setJoinLinkKind('secure')
                  setJoinLinkCustomMobile('')
                  setJoinLinkDialogOpen(true)
                }}
              >
                <ListItemIcon>
                  <WhatsAppIcon fontSize="small" sx={{ color: '#128c7e' }} />
                </ListItemIcon>
                <ListItemText primary="Send join link" secondary="WhatsApp, secure or public" />
              </MenuItem>
            )
          : null}
        {mediaCapabilities.canPlayRecording
          ? (close) => (
              <MenuItem
                key="recordings"
                onClick={() => {
                  close()
                  const path = mediaCapabilities.latestRecordingId
                    ? `/support-home/booked-events/${bookedEvent?.id}/recordings/${mediaCapabilities.latestRecordingId}`
                    : `/support-home/booked-events/${bookedEvent?.id}/recordings`
                  navigate(path, { state: { from: '/support-home/booked-events' } })
                }}
              >
                <ListItemIcon>
                  <VideoLibraryOutlinedIcon fontSize="small" sx={{ color: '#475569' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    mediaCapabilities.recordingCount > 1
                      ? `Recordings (${mediaCapabilities.recordingCount})`
                      : 'Recording'
                  }
                />
              </MenuItem>
            )
          : null}
        {mediaCapabilities.canManageRecordingPreference
          ? (close) => (
              <MenuItem
                key="recording-toggle"
                disabled={recordingToggling}
                onClick={() => handleRecordingToggle(close)}
              >
                <ListItemIcon>
                  <FiberManualRecordIcon
                    fontSize="small"
                    sx={{ color: mediaCapabilities.isRecordingEnabled ? '#047857' : '#b91c1c' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    recordingToggling
                      ? 'Updating…'
                      : mediaCapabilities.isRecordingEnabled
                        ? 'Recording is on'
                        : 'Recording is off'
                  }
                  secondary={
                    (mediaCapabilities.sourceLabel
                      ? `${mediaCapabilities.sourceLabel} · `
                      : '') +
                    (mediaCapabilities.isRecordingEnabled ? 'Tap to turn off' : 'Tap to turn on')
                  }
                />
              </MenuItem>
            )
          : null}
        {canChangeRtcProvider
          ? (close) => (
              <MenuItem
                key="rtc-provider"
                onClick={() => {
                  close()
                  setRtcSelection(bookedEvent?.rtcProviderOverride || '')
                  setRtcDialogOpen(true)
                }}
              >
                <ListItemIcon>
                  <VideocamOutlinedIcon fontSize="small" sx={{ color: '#7c3aed' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Change video provider"
                  secondary={
                    bookedEvent?.rtcProviderResolved
                      ? `Current: ${RTC_PROVIDER_LABELS[bookedEvent.rtcProviderResolved] || bookedEvent.rtcProviderResolved}`
                      : undefined
                  }
                />
              </MenuItem>
            )
          : null}
      </SupportActionMenuButton>

      <Dialog
        open={joinLinkDialogOpen}
        onClose={joinLinkSending ? undefined : () => setJoinLinkDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Send join link over WhatsApp</DialogTitle>
        <DialogContent>
          <Stack spacing={1.75} sx={{ mt: 0.5 }}>
            <ToggleButtonGroup
              value={joinLinkKind}
              exclusive
              size="small"
              fullWidth
              onChange={(event, nextValue) => {
                if (nextValue) {
                  setJoinLinkKind(nextValue)
                }
              }}
            >
              <ToggleButton value="secure">Secure link</ToggleButton>
              <ToggleButton value="public">Public link</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              {joinLinkKind === 'secure'
                ? 'Secure links sign the patient in before joining — preferred.'
                : 'Public links open the session without sign-in. Use when the patient cannot log in.'}
            </Typography>
            <TextField
              label="Custom mobile (optional)"
              size="small"
              fullWidth
              value={joinLinkCustomMobile}
              onChange={(event) => setJoinLinkCustomMobile(event.target.value)}
              placeholder="Leave empty to send to the patient"
              helperText="Include the country code, e.g. +91XXXXXXXXXX"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            color="inherit"
            onClick={() => setJoinLinkDialogOpen(false)}
            disabled={joinLinkSending}
          >
            Cancel
          </Button>
          <Button onClick={() => handleSendJoinLink(true)} disabled={joinLinkSending}>
            To Human Support
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSendJoinLink(false)}
            disabled={joinLinkSending}
          >
            {joinLinkSending
              ? 'Sending...'
              : joinLinkCustomMobile.trim()
                ? 'Send to Number'
                : 'Send to Patient'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rtcDialogOpen}
        onClose={rtcSaving ? undefined : () => setRtcDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Change video provider</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
              Overrides which video-call provider this session uses. Only possible until the
              joining link expires.
            </Typography>
            <TextField
              select
              label="Provider"
              size="small"
              fullWidth
              value={rtcSelection}
              onChange={(event) => setRtcSelection(event.target.value)}
            >
              <MenuItem value="">Default (no override)</MenuItem>
              {rtcProviderOptions.map((provider) => (
                <MenuItem key={provider} value={provider}>
                  {RTC_PROVIDER_LABELS[provider] || provider}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setRtcDialogOpen(false)} disabled={rtcSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRtcProviderSave} disabled={rtcSaving}>
            {rtcSaving ? 'Saving...' : 'Save Provider'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
